import React, { useEffect, useRef, useState, useCallback } from 'react';
import AuthView from 'components/AuthView';
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from 'react-router-dom';
import { updateDocumentTitle, toggleErrorDialog } from 'actions';
import serialize from 'form-serialize';
import buildActualPath from 'helpers/build-actual-path';
import { FILTER_DETAIL } from 'constants/routes';
import Input from 'components/Form/Input';
import FormGroup from 'components/Form/FormGroup';
import Form from 'components/Form';
import {
    TYPE_RADIO,
    TYPE_SELECT,
    TYPE_TEXT,
    TYPE_TEXTAREA,
} from 'components/Form/Input/input-type';
import { Translate, I18n } from 'react-redux-i18n';
import NavigationBar from 'components/NavigationBar';
import FlexGroup from 'components/FlexGroup';
import { Button } from 'reactstrap';
import { getPinnedOptions } from '../FilterPinStatus';
import { useGetAuthFilterTypeQuery } from 'redux/hook/useFilter';
import FilterCriteria from '../FilterCriteria';

import { useCreateFilterMutation } from 'redux/hook/useFilter';

import './new-filter.scss';

export const NAME = 'name';
export const TYPE = 'type';
export const PIN = 'pin';
export const DESCRIPTION = 'description';
export const CRITERIA = 'criteria';


const NewFilter = () => {
    const i18n = useSelector(state => state.i18n);
    const dispatch = useDispatch();
    const history = useHistory();

    useEffect(() => {
        dispatch(updateDocumentTitle('filters.new'));
    }, [dispatch, i18n]);

    const formRef = useRef();
    const [submitLoading, setSubmitLoading] = useState(false);

    const [name, setName] = useState('');
    const [isPinned, setIsPinned] = useState(false);
    const [description, setDescription] = useState('');
    const [type, setType] = useState();
    const criteria = useRef();

    const [, { data : authFilterTypeList } = {}] = useGetAuthFilterTypeQuery();
    const [authFilterTypeOpts, setAuthFilterTypeOpts] = useState([]);

    const [createFilter] = useCreateFilterMutation();


    useEffect(() => {
        let list = [];
        if (authFilterTypeList?.length) {
           list = authFilterTypeList.map(({ type }) => ({
                name: I18n.t(`filters.features_map.${ type }`),
                value: type,
            }));
            list.unshift({ name: '', value: '' });
        }
        setAuthFilterTypeOpts(list);
    }, [authFilterTypeList]);

    const handleFormChange = e => {
        const { name, value } = e.target;
        switch(name) {

            case NAME:
                setName(value);
                break;
            case PIN:
                setIsPinned((value === 'false') ? false : true);
                break;
            case DESCRIPTION:
                setDescription(value);
                break;
            case TYPE:
                if (value === '') {
                    setType();
                }
                else {
                    setType(Number(value));
                }
                break;
            default:
        }

    };
    const handleCreateSubmit = (e) => {
        e.preventDefault();
        if (submitLoading) return;
        setSubmitLoading(true);
    };

    const getFormData = () => {
        const formData = serialize(formRef.current, { hash: true });
        const isPinned = (formData[PIN] === 'false') ? false : true;
        return {
            [NAME]: formData[NAME],
            [TYPE]: Number(formData[TYPE]),
            [PIN]: isPinned,
            [DESCRIPTION]: formData[DESCRIPTION],
            [CRITERIA]: criteria.current,
        };

    };

    const handleCreateFilter = useCallback(async() => {

        let filterData = getFormData();
        try {

            const { data, error } = await createFilter({
                ...filterData,
            });
            if (error) {
                throw error;
            }
            if (data) {
                const { id } = data;
                history.replace(buildActualPath(FILTER_DETAIL, { filterId: id }));
            }
        }
        catch (error) {
            const { code } = error?.data || {};

            const message = (code !== undefined && code > 0) ? I18n.t(`filters.create_upd_error_${ code }`) : I18n.t('general_error_message');

            dispatch(toggleErrorDialog(message, () => {
                setSubmitLoading(false);
            }));
        }
    }, [dispatch, createFilter, history]);

    useEffect(()=> {
        if (submitLoading) {
            handleCreateFilter();
        }
    }, [submitLoading, handleCreateFilter]);

    return (
        <AuthView className="new-filter">
            <Form
                innerRef={ formRef }
                onSubmit={ handleCreateSubmit }
                onChange={ handleFormChange }
                inProgress={ submitLoading }
                inline
                stopSubmitByEnter
            >
                <NavigationBar title={ I18n.t('filters.new') } sticky>
                    <FlexGroup end gap>
                        <Button
                            type="submit"
                            color="primary"
                            disabled={ submitLoading }
                        >
                            <Translate value="save" />
                        </Button>
                    </FlexGroup>
                </NavigationBar>
                <FlexGroup className="filter-form-details">
                    <FormGroup title={ I18n.t('filters.details') } className="filter-config-block">
                        <Input
                            name={ NAME }
                            type={ TYPE_TEXT }
                            caption="filters.name"
                            value={ name }
                            maxLength={ 50 }
                            required
                        />
                        <FlexGroup className="filter-pin-group">
                            <Input
                                name={ PIN }
                                type={ TYPE_RADIO }
                                caption="filters.filter_pinned"
                                value={ getPinnedOptions() }
                                selected={ isPinned }
                                required
                            />
                        </FlexGroup>
                        <FlexGroup className="description-wrapper">
                            <Input
                                className="description"
                                name={ DESCRIPTION }
                                type={ TYPE_TEXTAREA }
                                rows={ 4 }
                                value={ description }
                                caption="filters.description"
                            />
                        </FlexGroup>
                        <Input
                            name={ TYPE }
                            type={ TYPE_SELECT }
                            caption="filters.filter_feature"
                            value={ authFilterTypeOpts }
                            selected={ type }
                            required
                        />
                    </FormGroup>
                </FlexGroup>
                <FilterCriteria
                    feature={ type }
                    criteria={ criteria }
                />
            </Form>
        </AuthView>
    );
};

export default NewFilter;
